import React, { useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Button,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import { useCollectionEmailSend } from '../hooks/useCollectionEmailSend';
import { CollectionEmailType } from '../model';
import { StatusChip } from './EmailHistoryChips';
import { buildEmailPreviewHtml } from '../../shared/buildEmailPreviewHtml';
import ForwardEmailModal from './ForwardEmailModal';

interface Props {
  open: boolean;
  emailSendId: number | null;
  onClose: () => void;
  emailType?: CollectionEmailType;
  location?: string;
}

const EmailHistoryDetailModal: React.FC<Props> = ({
  open,
  emailSendId,
  onClose,
  emailType = 'collection',
  location,
}) => {
  const { data, isLoading, isError } = useCollectionEmailSend(open ? emailSendId : null, emailType, location);
  const email = data?.data ?? null;
  const [forwardOpen, setForwardOpen] = useState(false);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6">{email ? email.company : 'Email Detail'}</Typography>
          {email && <StatusChip status={email.status} />}
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load email details.
          </Alert>
        )}
        {email && (
          <>
            {email.status === 'failed' && email.error_message && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {email.error_message}
              </Alert>
            )}

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="textSecondary">
                <strong>Subject:</strong> {email.subject}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                <Typography variant="body2" color="textSecondary">
                  <strong>To:</strong>
                </Typography>
                {email.to_emails && email.to_emails.length > 0 ? (
                  email.to_emails.map((addr) => <Chip key={addr} label={addr} size="small" />)
                ) : (
                  <Chip label="No email addresses" size="small" color="error" variant="outlined" />
                )}
              </Box>
              {email.queued_at && (
                <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                  <strong>Queued:</strong> {email.queued_at}
                </Typography>
              )}
              {email.delivered_at && (
                <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                  <strong>Delivered:</strong> {email.delivered_at}
                </Typography>
              )}
              {email.send_source === 'manual' && email.triggered_by_name && (
                <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                  <strong>Triggered by:</strong> {email.triggered_by_name}
                </Typography>
              )}
              {email.attachment_metadata && email.attachment_metadata.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="textSecondary">
                    <strong>Attachments:</strong>
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                    {email.attachment_metadata.map((a) => (
                      <Chip key={a.edoc_invoice_import_id} label={a.filename} size="small" variant="outlined" />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>

            {/* email.body is server-generated HTML; sandbox="" is defense-in-depth for the read-only preview. */}
            <iframe
              srcDoc={buildEmailPreviewHtml(email.body, email.sender_display_name, email.reply_to_email)}
              sandbox=""
              title="Email preview"
              style={{ width: '100%', height: 400, border: '1px solid #e0e0e0', borderRadius: 4 }}
            />
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {email && email.collections_client_id && (
          <Button startIcon={<ForwardToInboxIcon />} onClick={() => setForwardOpen(true)}>
            Forward
          </Button>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
      {email && (
        <ForwardEmailModal
          open={forwardOpen}
          email={email}
          onClose={() => setForwardOpen(false)}
          onSent={() => setForwardOpen(false)}
        />
      )}
    </Dialog>
  );
};

export default EmailHistoryDetailModal;
